// src/components/DocumentForgery/Batch/BatchHistory.jsx
// ─────────────────────────────────────────────────────
// Lists all previous batch jobs with filtering, pagination,
// and a "New Batch" button that routes to BatchUpload.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, Download, CheckCircle, XCircle,
  AlertTriangle, Clock, ChevronRight, BarChart2,
} from 'react-feather';
import { getBatchListHandler } from '../../../services/documentForgery';

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
};

const fmtDuration = (sec) => {
  if (!sec) return null;
  if (sec < 60) return `${Math.round(sec)}s`;
  return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
};

const fmtCost = (val) => {
  if (!val) return '$0.00';
  return `$${parseFloat(val).toFixed(4)}`;
};

// ── StatusChip ────────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = {
    pending:   { color: '#6b7280', bg: '#f3f4f6', label: 'Pending',   icon: <Clock size={10} /> },
    running:   { color: '#2563eb', bg: '#dbeafe', label: 'Running',   icon: <RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> },
    completed: { color: '#16a34a', bg: '#dcfce7', label: 'Completed', icon: <CheckCircle size={10} /> },
    partial:   { color: '#d97706', bg: '#fef3c7', label: 'Partial',   icon: <AlertTriangle size={10} /> },
    failed:    { color: '#dc2626', bg: '#fee2e2', label: 'Failed',    icon: <XCircle size={10} /> },
  };
  const c = cfg[status?.toLowerCase()] || cfg.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: c.bg, color: c.color,
      border: `1px solid ${c.color}33`,
      borderRadius: 20, fontWeight: 700,
      padding: '3px 9px', fontSize: 11, whiteSpace: 'nowrap',
    }}>
      {c.icon}{c.label}
    </span>
  );
};

// ── ProgressMini ──────────────────────────────────────────────────────────────
const ProgressMini = ({ pct, status }) => {
  const color = status === 'failed' ? '#dc2626' : status === 'completed' ? '#16a34a' : '#44ace4';
  return (
    <div style={{ height: 5, background: '#e5e7eb', borderRadius: 99, width: 80, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.max(pct, 2)}%`, background: color, borderRadius: 99, transition: 'width 0.4s' }} />
    </div>
  );
};

// ── Batch row ─────────────────────────────────────────────────────────────────
const BatchRow = ({ batch, onClick }) => {
  const done = batch.completed_count + batch.failed_count + batch.skipped_count;
  const isActive = ['pending', 'running'].includes(batch.status);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 90px 140px 90px 100px 32px',
        alignItems: 'center', gap: 12,
        padding: '14px 16px', borderRadius: 10,
        background: '#fff', border: '1px solid #e5e7eb',
        marginBottom: 8, cursor: 'pointer',
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor = '#44ace4'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
    >
      {/* Left: id + date */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#374151', fontWeight: 600 }}>
            {batch.ref_id?.slice(0, 8)}…
          </span>
          <span style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 20,
            background: '#f0f7ff', color: '#0284c7', fontWeight: 700,
          }}>
            {batch.source_display || batch.source}
          </span>
          {isActive && (
            <span style={{ fontSize: 10, color: '#2563eb', display: 'flex', alignItems: 'center', gap: 3 }}>
              <RefreshCw size={9} style={{ animation: 'spin 1s linear infinite' }} /> Live
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(batch.created_at)}</div>
      </div>

      {/* Documents + progress */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{batch.total_documents}</div>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>docs</div>
        {batch.total_documents > 0 && (
          <div style={{ marginTop: 4 }}>
            <ProgressMini pct={batch.progress_pct} status={batch.status} />
          </div>
        )}
      </div>

      {/* Counts */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {[
          { n: batch.completed_count, color: '#16a34a', label: '✓' },
          { n: batch.failed_count,    color: '#dc2626', label: '✗' },
          { n: batch.skipped_count,   color: '#9ca3af', label: '–' },
        ].map((s, i) => s.n > 0 && (
          <span key={i} style={{ fontSize: 11, fontWeight: 700, color: s.color }}>
            {s.label}{s.n}
          </span>
        ))}
        {done === 0 && <span style={{ fontSize: 11, color: '#d1d5db' }}>—</span>}
      </div>

      {/* Status */}
      <StatusChip status={batch.status} />

      {/* Cost + duration */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{fmtCost(batch.total_cost_usd)}</div>
        {batch.duration_seconds && (
          <div style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDuration(batch.duration_seconds)}</div>
        )}
        {batch.csv_url && (
          <a
            href={batch.csv_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#16a34a', fontWeight: 700, marginTop: 2 }}
          >
            <Download size={10} /> CSV
          </a>
        )}
      </div>

      <ChevronRight size={16} color="#d1d5db" />
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const BatchHistory = () => {
  const navigate = useNavigate();

  const [batches,     setBatches]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page,        setPage]        = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const PAGE_SIZE = 20;

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const res = await getBatchListHandler(params);
      const items = res?.data?.results || res?.data || [];
      setBatches(Array.isArray(items) ? items : []);
      setTotalCount(res?.data?.count || items.length);
    } catch {
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, page]);

  // Auto-refresh if any job is active
  useEffect(() => {
    const hasActive = batches.some(b => ['pending', 'running'].includes(b.status));
    if (!hasActive) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [batches]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const STATUS_FILTERS = [
    { value: '',          label: 'All' },
    { value: 'running',   label: 'Running' },
    { value: 'completed', label: 'Completed' },
    { value: 'partial',   label: 'Partial' },
    { value: 'failed',    label: 'Failed' },
  ];

  return (
    <div style={{ padding: '28px 24px', maxWidth: 980, margin: '0 auto' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 22, color: '#111827' }}>Batch Jobs</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            History of all bulk analysis runs. Click a row to see per-document results.
          </p>
        </div>
        <button
          onClick={() => navigate('/batch-upload/')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 8,
            background: '#44ace4', color: '#fff',
            fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
          }}
        >
          <Plus size={15} /> New Batch
        </button>
      </div>

      {/* ── Filter tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map(f => (
          <button key={f.value}
            onClick={() => { setStatusFilter(f.value); setPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20,
              border: `1px solid ${statusFilter === f.value ? '#44ace4' : '#e5e7eb'}`,
              background: statusFilter === f.value ? '#44ace4' : '#fff',
              color: statusFilter === f.value ? '#fff' : '#6b7280',
              fontWeight: 600, fontSize: 12, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {f.label}
          </button>
        ))}
        <button onClick={load}
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 20, background: '#fff', color: '#6b7280', cursor: 'pointer', fontSize: 12 }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* ── Column headers ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 90px 140px 90px 100px 32px',
        gap: 12, padding: '6px 16px',
        fontSize: 11, fontWeight: 700, color: '#9ca3af',
        textTransform: 'uppercase', letterSpacing: '0.4px',
      }}>
        <span>Batch / Date</span>
        <span style={{ textAlign: 'center' }}>Docs</span>
        <span style={{ textAlign: 'center' }}>Results</span>
        <span>Status</span>
        <span style={{ textAlign: 'right' }}>Cost</span>
        <span />
      </div>

      {/* ── Content ── */}
      {loading && batches.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, gap: 10, color: '#9ca3af' }}>
          <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: 14 }}>Loading batches…</span>
        </div>
      ) : batches.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#9ca3af' }}>
          <BarChart2 size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No batch jobs yet</div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>
            {statusFilter ? `No ${statusFilter} batches found.` : 'Submit your first bulk analysis to get started.'}
          </div>
          <button onClick={() => navigate('/dashboard/batch/new')}
            style={{ padding: '10px 20px', borderRadius: 8, background: '#44ace4', color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}>
            <Plus size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />New Batch
          </button>
        </div>
      ) : (
        <>
          {batches.map(b => (
            <BatchRow
              key={b.ref_id}
              batch={b}
              onClick={() => navigate(`/dashboard/batch/${b.ref_id}`)}
            />
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 24 }}>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: page === 1 ? '#f9fafb' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13, color: '#374151' }}>
                ← Prev
              </button>
              <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: page === totalPages ? '#f9fafb' : '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: 13, color: '#374151' }}>
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BatchHistory;